import { type MouseEvent, useCallback, useRef } from "react";
import { T, useTranslate } from "@tolgee/react";
import { ArrowUpDown, Clock3, KeyRound, MousePointerClick } from "lucide-react";

import { fadeBlurUp, MotionItem, StaggerContainer } from "#/components/ui/motion-primitives";
import { SectionBlock } from "./section-block";

const featureKeys = [
	{
		icon: ArrowUpDown,
		titleKey: "features.leverage-title",
		descriptionKey: "features.leverage-desc",
	},
	{
		icon: Clock3,
		titleKey: "features.24h-title",
		descriptionKey: "features.24h-desc",
	},
	{
		icon: KeyRound,
		titleKey: "features.custody-title",
		descriptionKey: "features.custody-desc",
	},
	{
		icon: MousePointerClick,
		titleKey: "features.onboarding-title",
		descriptionKey: "features.onboarding-desc",
	},
];

function useCardGlow() {
	const glowRef = useRef<HTMLDivElement>(null);

	const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
		const el = glowRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		el.style.setProperty("--glow-x", `${e.clientX - rect.left}px`);
		el.style.setProperty("--glow-y", `${e.clientY - rect.top}px`);
		el.style.setProperty("--glow-opacity", "1");
	}, []);

	const handleMouseLeave = useCallback(() => {
		glowRef.current?.style.setProperty("--glow-opacity", "0");
	}, []);

	return { glowRef, handleMouseMove, handleMouseLeave };
}

function GlowCard({ children }: { children: React.ReactNode }) {
	const { glowRef, handleMouseMove, handleMouseLeave } = useCardGlow();
	return (
		/* biome-ignore lint/a11y/noStaticElementInteractions: decorative mouse tracking only */
		<div
			ref={glowRef}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			className="relative overflow-hidden rounded-[24px] border border-[var(--color-theme-static-white)]/8 bg-[linear-gradient(180deg,rgb(255_255_255_/_0.04)_0%,rgb(255_255_255_/_0.02)_100%)] p-6 shadow-[0_18px_36px_rgb(0_0_0_/_0.14)] transition-[border-color] duration-300 hover:border-primary/20"
			style={
				{
					"--glow-opacity": "0",
				} as React.CSSProperties
			}
		>
			{/* Mouse-following radial glow */}
			<div
				className="pointer-events-none absolute inset-0 transition-opacity duration-300"
				style={{
					opacity: "var(--glow-opacity)",
					background:
						"radial-gradient(320px circle at var(--glow-x) var(--glow-y), color-mix(in oklab, var(--primary) 18%, transparent), transparent 60%)",
				}}
			/>
			<div className="relative">{children}</div>
		</div>
	);
}

export function FeaturesSection() {
	const { t } = useTranslate();
	const features = featureKeys.map((feature) => ({
		...feature,
		title: t(feature.titleKey),
		description: t(feature.descriptionKey),
	}));

	return (
		<SectionBlock
			id="core-features"
			label={t("features.section-label")}
			title={
				<T
					keyName="features.section-title"
					params={{
						light: <span />,
						accent: <span className="text-primary" />,
						break: <br />,
					}}
				/>
			}
			description={t("features.section-description")}
			centered
		>
			<StaggerContainer
				className="mx-auto mb-18 grid w-[calc(100%-24px)] max-w-275 gap-4 md:w-[calc(100%-72px)] md:grid-cols-2"
				stagger={0.1}
			>
				{features.map((feature) => {
					const Icon = feature.icon;
					return (
						<MotionItem key={feature.title} variants={fadeBlurUp}>
							<GlowCard>
								<div className="inline-flex rounded-2xl border border-primary/14 bg-primary/10 p-3 text-primary">
									<Icon className="h-5 w-5" />
								</div>
								<h3 className="mt-5 text-title2 text-foreground">
									{feature.title}
								</h3>
								<p className="mt-3 text-body1 text-muted-foreground">
									{feature.description}
								</p>
							</GlowCard>
						</MotionItem>
					);
				})}
			</StaggerContainer>
		</SectionBlock>
	);
}
